<template>
  <div class="flex items-center gap-1.5 sm:gap-2 shrink-0">
    <!-- Up Next: show lock icon instead of votes -->
    <div v-if="isUpNext" class="flex items-center gap-2">
      <span
        class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-md"
      >
        <i class="pi pi-lock text-xs"></i>
        {{ t("event.upNext") }}
      </span>
    </div>

    <div
      v-else-if="showKaraokeIndicator"
      class="w-8 h-8 sm:w-10 sm:h-10 rounded-md flex items-center justify-center text-xs sm:text-sm"
      :class="
        isKaraokeQueued
          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
      "
      :title="
        isKaraokeQueued
          ? t('event.karaoke.alreadyQueuedTooltip')
          : t('event.karaoke.tapToRegisterTooltip')
      "
      :data-testid="isKaraokeQueued ? 'song-karaoke-queued-indicator' : 'song-karaoke-available-indicator'"
    >
      <i
        :class="isKaraokeQueued ? 'pi pi-exclamation-triangle' : 'pi pi-microphone'"
        class="text-xs sm:text-sm"
      ></i>
    </div>

    <template v-else-if="showVoteCount || showVoteButton">
      <div v-if="showVoteCount" class="text-right leading-none" data-testid="song-vote-count">
        <div
          class="text-gray-900 dark:text-white tabular-nums"
          :class="size === 'lg' ? 'text-xl sm:text-3xl font-bold' : 'text-sm font-semibold'"
        >
          {{ voteCount }}
        </div>
        <div
          class="text-gray-500 uppercase dark:text-gray-400 mt-0.5"
          :class="size === 'lg' ? 'text-[10px] sm:text-xs' : 'text-[10px]'"
        >
          {{ voteCount === 1 ? t("event.vote") : t("event.votes") }}
        </div>
      </div>

      <template v-if="showVoteButton">
        <button
          v-if="isVoting || (!hasVoted && !isPerformed)"
          type="button"
          :disabled="isVoting || !canVote"
          :aria-disabled="isVoteLocked || isVoting || !canVote"
          :title="t('event.voteThisSongTooltip')"
          :class="{
            'pointer-events-none': isVoteLocked && !isVoting,
            'bg-teal-500/80 hover:bg-teal-500/80 text-white/90': isVoteLocked && !isVoting
          }"
          class="w-8 h-8 sm:w-10 sm:h-10 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center text-xs sm:text-sm font-medium shadow-sm hover:shadow-md disabled:shadow-none"
          data-testid="song-vote-button"
          @click="handleVote"
        >
          <i v-if="isVoting" class="pi pi-spin pi-spinner text-xs sm:text-sm"></i>
          <i v-else class="pi pi-thumbs-up-fill text-xs sm:text-sm"></i>
        </button>

        <!-- Voted, not yet played (icon only) -->
        <div
          v-else-if="hasVoted && !isPerformed"
          class="w-8 h-8 sm:w-10 sm:h-10 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded-md flex items-center justify-center text-[11px]"
          :title="t('event.votedTooltip')"
          data-testid="song-voted-indicator"
        >
          <i class="pi pi-check text-xs"></i>
        </div>

        <!-- Played (with or without vote, icon only) -->
        <div
          v-else
          class="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-md flex items-center justify-center text-[11px] cursor-not-allowed"
          :title="t('event.performedTooltip')"
          data-testid="song-performed-indicator"
        >
          <i v-if="hasVoted" class="pi pi-check text-xs"></i>
          <i v-else class="pi pi-thumbs-up text-xs"></i>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup>
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  voteCount: {
    type: Number,
    default: 0
  },
  isPerformed: {
    type: Boolean,
    default: false
  },
  isUpNext: {
    type: Boolean,
    default: false
  },
  canVote: {
    type: Boolean,
    default: true
  },
  hasVoted: {
    type: Boolean,
    default: false
  },
  isVoting: {
    type: Boolean,
    default: false
  },
  isVoteLocked: {
    type: Boolean,
    default: false
  },
  showVoteCount: {
    type: Boolean,
    default: true
  },
  showVoteButton: {
    type: Boolean,
    default: true
  },
  showKaraokeIndicator: {
    type: Boolean,
    default: false
  },
  isKaraokeQueued: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: "sm",
    validator: (value) => ["sm", "lg"].includes(value)
  }
});

const emit = defineEmits(["vote"]);

const handleVote = () => {
  if (
    props.isVoting ||
    props.isVoteLocked ||
    props.isPerformed ||
    !props.canVote ||
    props.hasVoted
  ) {
    return;
  }
  emit("vote");
};
</script>
